import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Globe, Menu, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { siteSettings } from '../utils/mockData';

const Header = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    // If not on landing page, navigate to landing page first
    if (location.pathname !== '/') {
      navigate('/', { state: { scrollTo: sectionId } });
      setIsMobileMenuOpen(false);
      return;
    }
    
    // If on landing page, scroll to section
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setIsMobileMenuOpen(false);
    }
  };

  // Handle scroll to section after navigation
  useEffect(() => {
    if (location.pathname === '/' && location.state?.scrollTo) {
      setTimeout(() => {
        const element = document.getElementById(location.state.scrollTo);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    }
  }, [location]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-white/95 backdrop-blur-md shadow-md' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <img
              src={siteSettings.logo_url || '/logo.png'}
              alt="Manalogi Logo"
              className="h-10 w-auto object-contain transform group-hover:scale-105 transition-transform"
            />
            <span className="text-xl font-bold text-gray-800 group-hover:text-cyan-600 transition-colors">
              {siteSettings.company_name || 'manalogi'}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            <button
              onClick={() => scrollToSection('home')}
              className="text-gray-700 hover:text-cyan-600 transition-colors font-medium"
            >
              {t.nav.home}
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-gray-700 hover:text-cyan-600 transition-colors font-medium"
            >
              {t.nav.about}
            </button>
            <button
              onClick={() => scrollToSection('solutions')}
              className="text-gray-700 hover:text-cyan-600 transition-colors font-medium"
            >
              {t.nav.solutions}
            </button>
            <button
              onClick={() => scrollToSection('products')}
              className="text-gray-700 hover:text-cyan-600 transition-colors font-medium"
            >
              {t.nav.products}
            </button>
            <button
              onClick={() => scrollToSection('clients')}
              className="text-gray-700 hover:text-cyan-600 transition-colors font-medium"
            >
              {t.nav.clients}
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-gray-700 hover:text-cyan-600 transition-colors font-medium"
            >
              {t.nav.contact}
            </button>
          </nav>

          {/* Language Toggle & Mobile Menu */}
          <div className="flex items-center space-x-4">
            <button
              onClick={toggleLanguage}
              className="flex items-center space-x-1 px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 transition-colors"
            >
              <Globe className="w-4 h-4 text-gray-700" />
              <span className="text-sm font-medium text-gray-700">
                {language.toUpperCase()}
              </span>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-gray-700" />
              ) : (
                <Menu className="w-6 h-6 text-gray-700" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden py-4 border-t bg-white/95 backdrop-blur-md">
            <nav className="flex flex-col space-y-4">
              <button
                onClick={() => scrollToSection('home')}
                className="text-left text-gray-700 hover:text-cyan-600 transition-colors font-medium px-4 py-2"
              >
                {t.nav.home}
              </button>
              <button
                onClick={() => scrollToSection('about')}
                className="text-left text-gray-700 hover:text-cyan-600 transition-colors font-medium px-4 py-2"
              >
                {t.nav.about}
              </button>
              <button
                onClick={() => scrollToSection('solutions')}
                className="text-left text-gray-700 hover:text-cyan-600 transition-colors font-medium px-4 py-2"
              >
                {t.nav.solutions}
              </button>
              <button
                onClick={() => scrollToSection('products')}
                className="text-left text-gray-700 hover:text-cyan-600 transition-colors font-medium px-4 py-2"
              >
                {t.nav.products}
              </button>
              <button
                onClick={() => scrollToSection('clients')}
                className="text-left text-gray-700 hover:text-cyan-600 transition-colors font-medium px-4 py-2"
              >
                {t.nav.clients}
              </button>
              <button
                onClick={() => scrollToSection('contact')}
                className="text-left text-gray-700 hover:text-cyan-600 transition-colors font-medium px-4 py-2"
              >
                {t.nav.contact}
              </button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
